import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  RefreshCw,
  Download,
  ShieldCheck,
  Search,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Calendar,
  UserCheck,
  Wallet,
  HelpCircle,
} from "lucide-react";
import { API } from "@/lib/constants";
import { useLanguage } from "@/contexts/LanguageContext";
import { bilingualCountry } from "@/lib/countries";

// Rev 7.12 (Feb 26, 2026) — Owner-directed visitor-admin readability
// rebuild. The old mega-row used unlabeled abbreviations (EXP, VEH,
// BUDP, BUDM, "reg.") crammed into ten columns. Replaced with a clean
// summary table plus an in-row expandable "View details" panel that
// shows every field with full human-readable labels. Zero behavior
// changes: search, day filter, Apply, CSV/XLSX/full-XLSX exports and
// the check-in endpoints are all preserved untouched.
const DAYS = ["dec10", "dec11", "dec12"];
const DAY_LABEL_SHORT = {
  dec10: "Thu · Dec 10",
  dec11: "Fri · Dec 11",
  dec12: "Sat · Dec 12",
};
const DAY_LABEL_FULL = {
  dec10: "Thursday, December 10, 2026",
  dec11: "Friday, December 11, 2026",
  dec12: "Saturday, December 12, 2026",
};
const DAY_LABEL_BTN = {
  dec10: "Dec 10",
  dec11: "Dec 11",
  dec12: "Dec 12",
};

const SEX_LABEL = { male: "Male", female: "Female" };
const AGE_BRACKET_LABEL = {
  under_18: "Under 18",
  "18_24": "18–24",
  "25_34": "25–34",
  "35_44": "35–44",
  "45_54": "45–54",
  "55_plus": "55+",
};

// Full-text labels mirror the exact copy used on the visitor form and
// in the Excel export — no internal tokens, no abbreviations. Kept in
// sync with backend/server.py::_EXPORT_* dictionaries.
const IEXP_LABEL = {
  vehicle_displays: "Vehicle displays",
  sim_racing: "Sim racing",
  community_meet: "Meeting owners and the car community",
  no_preference: "No specific preference",
  f1_memorabilia: "F1 memorabilia (legacy)",
};
const IVEH_LABEL = {
  classic: "Classic cars",
  modified_jdm: "Modified / JDM cars",
  supercars: "Supercars",
  motorcycles: "Motorcycles",
  no_preference: "No specific preference",
};
const IMUL_LABEL = {
  different_vehicles_daily: "Different vehicles each day",
  sim_racing: "Sim racing",
  meeting_people: "Meeting people",
  other_activities: "Other activities",
  not_sure_yet: "Not sure yet",
};
const BUDP_LABEL = {
  le_150k: "SAR 150,000 or less",
  "150k_300k": "SAR 150,001–300,000",
  "300k_500k": "SAR 300,001–500,000",
  gt_500k: "More than SAR 500,000",
  not_considering: "Not currently considering purchasing a vehicle",
  prefer_not_to_say: "Prefer not to say",
};
const BUDM_LABEL = {
  le_25k: "SAR 25,000 or less",
  "25k_50k": "SAR 25,001–50,000",
  "50k_100k": "SAR 50,001–100,000",
  gt_100k: "More than SAR 100,000",
  not_considering: "Not currently considering modifications",
  prefer_not_to_say: "Prefer not to say",
};
const ASSIST_ANSWER = {
  yes: "Yes",
  no: "No",
  prefer_not_to_say: "Prefer not to say",
};
const ASSIST_NEED = {
  mobility: "Mobility or wheelchair access",
  hearing: "Hearing assistance",
  visual: "Visual assistance",
  other: "Other assistance",
};

const formatCheckinTimestamp = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

export default function AdminVisitorsPanel({ token }) {
  const { isRTL } = useLanguage(); // eslint-disable-line no-unused-vars
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [day, setDay] = useState("");
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [listR, statsR, cfgR] = await Promise.all([
        axios.get(`${API}/admin/visitors`, {
          ...authHeader,
          params: { q: q || undefined, day: day || undefined, limit: 500 },
        }),
        axios.get(`${API}/admin/visitors/stats`, authHeader),
        axios
          .get(`${API}/_admin/visitor-config`, authHeader)
          .catch(() => ({ data: null })),
      ]);
      setRows(listR.data?.visitors || []);
      setStats(statsR.data || null);
      setConfig(cfgR.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Visitors fetch failed");
    } finally {
      setLoading(false);
    }
  }, [token, q, day, authHeader]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const checkIn = async (v, dayKey) => {
    if (!window.confirm(`On-site check-in: ${v.visitor_ref} on ${DAY_LABEL_FULL[dayKey] || dayKey}. Continue?`)) return;
    try {
      const r = await axios.post(
        `${API}/admin/visitors/${encodeURIComponent(v.id)}/checkin`,
        { day: dayKey },
        authHeader,
      );
      if (r.data?.already_checked_in)
        toast(`${v.visitor_ref} already checked in for ${DAY_LABEL_SHORT[dayKey]}`);
      else toast.success(`${v.visitor_ref} checked in for ${DAY_LABEL_SHORT[dayKey]}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-in failed");
    }
  };

  const csvHref = `${API}/admin/visitors/export.csv`;
  const downloadCsv = async () => {
    try {
      const r = await axios.get(csvHref, { ...authHeader, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "winter2026-visitors.csv";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("CSV export failed");
    }
  };

  const downloadXlsx = async () => {
    try {
      const r = await axios.get(`${API}/admin/visitors/export.xlsx`, {
        ...authHeader,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "winter2026-visitors.xlsx";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("XLSX export failed");
    }
  };

  // Feb 2026 owner-approved "Export All Visitors (Excel)" — full,
  // read-only bulk workbook (three sheets: Visitor Registrations,
  // Family Members, Export Summary).
  const [fullXlsxLoading, setFullXlsxLoading] = useState(false);
  const downloadFullXlsx = async () => {
    if (fullXlsxLoading) return;
    setFullXlsxLoading(true);
    try {
      const r = await axios.get(`${API}/admin/visitors/export-full.xlsx`, {
        ...authHeader,
        responseType: "blob",
      });
      let filename = "";
      const cd =
        r.headers?.["content-disposition"] ||
        r.headers?.["Content-Disposition"];
      if (cd) {
        const m = /filename="([^"]+)"/i.exec(cd);
        if (m) filename = m[1];
      }
      if (!filename) {
        const stamp = new Date()
          .toISOString()
          .replace(/[-T:]/g, "")
          .slice(0, 12);
        filename = `THE_GATHERING_Visitor_Registrations_${stamp}_Riyadh.xlsx`;
      }
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Export All Visitors (Excel) downloaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Full Excel export failed");
    } finally {
      setFullXlsxLoading(false);
    }
  };

  return (
    <div
      className="mt-10 border border-white/10 bg-white/[0.02] p-5 sm:p-6"
      data-testid="admin-visitors-panel"
      dir="ltr"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#F5C518]" />
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#F5C518]">
              Visitor Registrations
            </div>
            <div className="font-heading font-extrabold text-white text-xl sm:text-2xl">
              Winter 2026 · Visitors
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={fetchAll}
            disabled={loading}
            data-testid="visitors-refresh-btn"
            className="text-sm px-3 py-2 border border-white/20 text-white/80 hover:border-[#F5C518] hover:text-[#F5C518] transition disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />{" "}
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            data-testid="visitors-export-csv-btn"
            className="text-sm px-3 py-2 border border-[#F5C518] text-[#F5C518] hover:bg-[#F5C518] hover:text-black transition inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button
            onClick={downloadXlsx}
            data-testid="visitors-export-xlsx-btn"
            className="text-sm px-3 py-2 border border-[#F5C518] text-[#F5C518] hover:bg-[#F5C518] hover:text-black transition inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Export XLSX
          </button>
          <button
            onClick={downloadFullXlsx}
            disabled={fullXlsxLoading}
            data-testid="visitors-export-full-xlsx-btn"
            aria-busy={fullXlsxLoading}
            aria-label="Export All Visitors as an Excel workbook"
            className="text-sm px-3 py-2 border border-[#F5C518] bg-[#F5C518]/10 text-[#F5C518] hover:bg-[#F5C518] hover:text-black transition inline-flex items-center gap-2 font-extrabold uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Download
              className={`w-4 h-4 ${fullXlsxLoading ? "animate-pulse" : ""}`}
            />
            {fullXlsxLoading ? "Preparing…" : "Export All Visitors (Excel)"}
          </button>
        </div>
      </div>

      {config && (
        <div
          className="mb-4 border border-amber-500/40 bg-amber-500/[0.05] p-3 text-sm text-amber-200/90 flex items-start gap-2"
          data-testid="visitors-config-banner"
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
          <div className="leading-relaxed">
            Live email flags · confirmation:{" "}
            <strong>{config.confirmation_enabled ? "ON" : "OFF"}</strong> ·
            organizer notification:{" "}
            <strong>{config.admin_notify_enabled ? "ON" : "OFF"}</strong> ·
            prize draw:{" "}
            <strong>{config.prize_draw_enabled ? "ON" : "OFF"}</strong>. Sender{" "}
            <span className="font-mono">{config.sender_email_masked}</span> ·
            Reply-To{" "}
            <span className="font-mono">{config.reply_to_masked}</span> ·
            Organizer recipient{" "}
            <span className="font-mono">
              {config.admin_notify_recipient_masked}
            </span>
            . Templates viewable via the preview links below — nothing sends
            until both flags are ON.
          </div>
        </div>
      )}

      {stats && (
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4"
          data-testid="visitors-stats"
        >
          <Stat
            label="Registrations (unique)"
            value={stats.total_registrations}
            testid="stat-total-regs"
          />
          <Stat
            label="Projected headcount (sum of groups)"
            value={stats.total_planned_headcount}
            testid="stat-planned-head"
          />
          <Stat
            label="Planned by day"
            value={DAYS.map(
              (d) =>
                `${DAY_LABEL_SHORT[d].split("·")[0].trim()}: ${stats.planned_by_day?.[d] || 0}`,
            ).join(" · ")}
            testid="stat-planned-by-day"
          />
          <Stat
            label="Verified on-site by day"
            value={DAYS.map(
              (d) =>
                `${DAY_LABEL_SHORT[d].split("·")[0].trim()}: ${stats.verified_by_day?.[d] || 0}`,
            ).join(" · ")}
            testid="stat-verified-by-day"
          />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchAll();
            }}
            placeholder="Search reference / name / email / mobile"
            data-testid="visitors-search-input"
            className="w-full bg-black/40 border border-white/15 pl-9 pr-3 py-2 text-white text-sm placeholder:text-white/30 focus:border-[#F5C518] focus:outline-none"
          />
        </div>
        <select
          value={day}
          onChange={(e) => setDay(e.target.value)}
          data-testid="visitors-day-filter"
          className="bg-black/40 border border-white/15 px-3 py-2 text-white text-sm"
        >
          <option value="">All days</option>
          {DAYS.map((d) => (
            <option key={d} value={d}>
              {DAY_LABEL_SHORT[d]}
            </option>
          ))}
        </select>
        <button
          onClick={fetchAll}
          data-testid="visitors-apply-filter"
          className="text-sm px-3 py-2 border border-white/20 text-white/80 hover:text-[#F5C518] hover:border-[#F5C518] transition"
        >
          Apply
        </button>
      </div>

      {rows.length === 0 ? (
        <div
          className="text-white/60 text-sm py-8 text-center"
          data-testid="visitors-empty"
        >
          No visitor registrations match this filter yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full text-sm text-white/90 border-collapse"
            data-testid="visitors-table"
          >
            <thead className="text-[11px] uppercase tracking-widest text-white/60">
              <tr className="border-b border-white/15">
                <th className="text-left py-3 px-3 font-semibold w-[44px]">&nbsp;</th>
                <th className="text-left py-3 px-3 font-semibold">Reference</th>
                <th className="text-left py-3 px-3 font-semibold">Lead visitor</th>
                <th className="text-left py-3 px-3 font-semibold w-[80px]">Group</th>
                <th className="text-left py-3 px-3 font-semibold">Planned days</th>
                <th className="text-left py-3 px-3 font-semibold w-[110px]">Assistance</th>
                <th className="text-left py-3 px-3 font-semibold">Check-in</th>
                <th className="text-left py-3 px-3 font-semibold w-[130px]">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => {
                const checked = {};
                (v.checkins || []).forEach((c) => {
                  checked[c.day] = c.at;
                });
                const isOpen = expanded.has(v.id);
                const assistAns = (v.assistance?.answer || "").toLowerCase();
                const plannedDays = Array.isArray(v.planned_days) ? v.planned_days : [];
                return (
                  <React.Fragment key={v.id}>
                    <tr
                      className={`border-b border-white/10 ${isOpen ? "bg-[#F5C518]/[0.04]" : "hover:bg-white/[0.02]"} transition`}
                      data-testid={`visitor-row-${v.visitor_ref}`}
                    >
                      <td className="py-3 px-3 align-top">
                        <button
                          onClick={() => toggleExpanded(v.id)}
                          data-testid={`visitor-row-toggle-${v.visitor_ref}`}
                          aria-label={isOpen ? "Collapse details" : "Expand details"}
                          aria-expanded={isOpen}
                          className="w-8 h-8 inline-flex items-center justify-center border border-white/15 text-white/70 hover:border-[#F5C518] hover:text-[#F5C518] transition"
                        >
                          {isOpen ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td
                        className="py-3 px-3 align-top font-mono text-[#F5C518] whitespace-nowrap"
                        dir="ltr"
                      >
                        {v.visitor_ref}
                      </td>
                      <td className="py-3 px-3 align-top text-white font-semibold">
                        {v.full_name || <span className="text-white/40">—</span>}
                      </td>
                      <td className="py-3 px-3 align-top">
                        <span className="inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 bg-white/10 text-white font-bold text-sm">
                          {v.total_attending}
                        </span>
                      </td>
                      <td className="py-3 px-3 align-top text-white/85">
                        {plannedDays.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {plannedDays.map((d) => (
                              <span
                                key={d}
                                className="inline-block px-2 py-0.5 border border-white/20 bg-black/40 text-[13px]"
                              >
                                {DAY_LABEL_SHORT[d] || d}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-white/40">—</span>
                        )}
                      </td>
                      <td
                        className="py-3 px-3 align-top"
                        data-testid={`visitor-assist-badge-${v.visitor_ref}`}
                      >
                        {assistAns === "yes" ? (
                          <span className="inline-block px-2 py-1 bg-amber-500 text-black text-xs font-extrabold uppercase tracking-wider">
                            Yes
                          </span>
                        ) : assistAns === "no" ? (
                          <span className="inline-block px-2 py-1 bg-white/10 text-white/80 text-xs font-bold uppercase tracking-wider">
                            No
                          </span>
                        ) : assistAns ? (
                          <span className="inline-block px-2 py-1 bg-white/10 text-white/70 text-xs font-bold uppercase tracking-wider">
                            n/a
                          </span>
                        ) : (
                          <span className="text-white/40">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3 align-top">
                        <div className="flex flex-wrap gap-1.5">
                          {DAYS.map((d) => (
                            <button
                              key={d}
                              onClick={() => checkIn(v, d)}
                              disabled={!!checked[d]}
                              data-testid={`checkin-${v.visitor_ref}-${d}`}
                              title={
                                checked[d]
                                  ? `Checked in ${formatCheckinTimestamp(checked[d])}`
                                  : `Check in for ${DAY_LABEL_FULL[d]}`
                              }
                              className={`text-xs px-2.5 py-1.5 border ${checked[d] ? "border-[#22C55E]/60 text-[#22C55E] bg-[#22C55E]/[0.08] cursor-default" : "border-white/25 text-white/85 hover:border-[#F5C518] hover:text-[#F5C518]"} transition`}
                            >
                              {checked[d] ? (
                                <span className="inline-flex items-center gap-1">
                                  <CheckCircle2 className="w-3.5 h-3.5" />{" "}
                                  {DAY_LABEL_BTN[d]}
                                </span>
                              ) : (
                                DAY_LABEL_BTN[d]
                              )}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-3 align-top">
                        <button
                          onClick={() => toggleExpanded(v.id)}
                          data-testid={`visitor-view-details-${v.visitor_ref}`}
                          aria-expanded={isOpen}
                          className={`text-sm px-3 py-1.5 border ${isOpen ? "border-[#F5C518] text-[#F5C518] bg-[#F5C518]/[0.08]" : "border-white/25 text-white/85 hover:border-[#F5C518] hover:text-[#F5C518]"} transition inline-flex items-center gap-1.5 whitespace-nowrap`}
                        >
                          {isOpen ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          {isOpen ? "Hide details" : "View details"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr
                        className="border-b border-white/15 bg-black/40"
                        data-testid={`visitor-details-${v.visitor_ref}`}
                      >
                        <td colSpan={8} className="p-0">
                          <VisitorDetails v={v} checked={checked} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 border-t border-white/10 pt-3 text-xs text-white/50">
        <div className="uppercase tracking-widest text-[#F5C518] mb-1">
          Email template previews (admin only, does not send)
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <a
            className="hover:text-[#F5C518]"
            href={`${API}/_admin/preview/visitor_email/solo_dec12`}
            target="_blank"
            rel="noreferrer"
            data-testid="preview-visitor-email-solo"
          >
            Registrant · solo (Dec 12)
          </a>
          <a
            className="hover:text-[#F5C518]"
            href={`${API}/_admin/preview/visitor_email/family_all_days`}
            target="_blank"
            rel="noreferrer"
            data-testid="preview-visitor-email-family"
          >
            Registrant · group (all 3 days)
          </a>
          <a
            className="hover:text-[#F5C518]"
            href={`${API}/_admin/preview/visitor_admin_email/solo`}
            target="_blank"
            rel="noreferrer"
            data-testid="preview-visitor-admin-solo"
          >
            Organizer · solo
          </a>
          <a
            className="hover:text-[#F5C518]"
            href={`${API}/_admin/preview/visitor_admin_email/large_group_10`}
            target="_blank"
            rel="noreferrer"
            data-testid="preview-visitor-admin-group10"
          >
            Organizer · group of 10
          </a>
        </div>
      </div>

      {/* Rev 7.6 — admin-only draft prize-eligibility clause. Preserved
          verbatim per owner directive. */}
      <div
        className="mt-5 border-2 border-amber-500/50 bg-amber-500/[0.06] p-4"
        data-testid="admin-prize-eligibility-draft"
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] px-2 py-0.5 bg-amber-500 text-black font-bold uppercase tracking-wider">
            Draft · Not Public
          </span>
          <span className="text-xs text-amber-200/90 uppercase tracking-widest">
            Prize-eligibility clause · owner review
          </span>
        </div>
        <div
          className="text-white text-sm leading-relaxed font-medium mb-2"
          dir="ltr"
          data-testid="admin-prize-eligibility-en"
        >
          IMPORTANT: One ticket covers your group. If it wins a prize, only the
          person named on this registration may claim it.
        </div>
        <div
          className="text-white text-sm leading-relaxed font-medium mb-3 text-right"
          lang="ar"
          dir="rtl"
          style={{
            fontFamily:
              "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif",
          }}
          data-testid="admin-prize-eligibility-ar"
        >
          مهم: تذكرة واحدة تشمل مجموعتك. وفي حال فوزها بجائزة، يحق لصاحب الاسم المسجّل فقط استلامها.
        </div>
        <div
          className="border-t border-amber-500/30 pt-2 text-xs text-amber-100/80 leading-relaxed"
          dir="ltr"
        >
          <strong>Winner-determination rules (draft):</strong> Ticket-level
          entries only · at most one verified daily check-in entry per group
          ticket per day · lead registrant (name + mobile on file) is the sole
          prize claimant · accompanying persons receive no independent tickets
          or entries. Publication gated by{" "}
          <span className="font-mono">VISITOR_PRIZE_DRAW_ENABLED</span>=
          <span className="font-mono">
            {config?.prize_draw_enabled ? "true" : "false"}
          </span>
          .
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-white/10 bg-black/40 p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-white/60">
        {label}
      </div>
      <div className="text-white font-bold text-lg mt-1 leading-tight">
        {value ?? "—"}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expandable details panel — renders every stored field with full labels.
// Uses a 2-column grid on ≥md screens and stacks cleanly on smaller widths.
// ---------------------------------------------------------------------------
function VisitorDetails({ v, checked }) {
  const persons = Array.isArray(v.persons) ? v.persons : [];
  const legacyBrackets = [v.age_bracket, ...(v.companion_ages || [])].filter(Boolean);
  const interests = v.interests || {};
  const exp = Array.isArray(interests.experience) ? interests.experience : [];
  const veh = Array.isArray(interests.vehicles) ? interests.vehicles : [];
  const mul = Array.isArray(interests.multiday) ? interests.multiday : [];
  const budget = v.budget || {};
  const budP = budget.purchase || "";
  const budM = budget.modification || "";
  const assist = v.assistance || {};
  const assistAns = (assist.answer || "").toLowerCase();
  const assistNeeds = Array.isArray(assist.needs) ? assist.needs : [];
  const assistNote = assist.note || "";
  const plannedDays = Array.isArray(v.planned_days) ? v.planned_days : [];
  const countryLabel = bilingualCountry(v.country || "") || v.country || "";

  return (
    <div className="px-5 py-5 sm:px-6 sm:py-6 border-l-4 border-[#F5C518]/60">
      {/* Header strip: reference + created stamp */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-5 pb-3 border-b border-white/10">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[#F5C518] text-base" dir="ltr">
            {v.visitor_ref}
          </span>
          <span className="text-white font-semibold text-base">
            {v.full_name}
          </span>
        </div>
        {v.created_at && (
          <span className="text-xs text-white/50">
            Registered {formatCheckinTimestamp(v.created_at)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        {/* Contact */}
        <DetailSection icon={Mail} title="Contact">
          <DetailRow label="Full name" value={v.full_name} />
          <DetailRow
            label="Email"
            value={
              v.email ? (
                <span className="font-mono break-all text-white/90">
                  {v.email}
                </span>
              ) : (
                <span className="text-white/40 italic">Not provided</span>
              )
            }
          />
          <DetailRow
            label="Mobile"
            value={
              v.mobile ? (
                <span className="font-mono" dir="ltr">
                  {v.mobile}
                </span>
              ) : (
                <span className="text-white/40">—</span>
              )
            }
          />
          <DetailRow
            label="City"
            value={
              v.city ? (
                <span className="text-white/90">{v.city}</span>
              ) : (
                <span className="text-white/40">—</span>
              )
            }
          />
          <DetailRow
            label="Country"
            value={
              countryLabel ? (
                <span className="text-white/90">{countryLabel}</span>
              ) : (
                <span className="text-white/40">—</span>
              )
            }
          />
        </DetailSection>

        {/* Group & attendees */}
        <DetailSection icon={Users} title="Group and attendees">
          <DetailRow
            label="Group size"
            value={
              <span className="inline-flex items-center gap-2">
                <span className="inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 bg-[#F5C518] text-black font-bold text-sm">
                  {v.total_attending}
                </span>
                <span className="text-white/70 text-sm">
                  {v.total_attending === 1
                    ? "person attending"
                    : "people attending"}
                </span>
              </span>
            }
          />
          <div className="mt-2">
            <div className="text-xs uppercase tracking-wider text-white/50 mb-2">
              Attendees
            </div>
            {persons.length > 0 ? (
              <ul className="space-y-1.5" data-testid={`visitor-attendees-${v.visitor_ref}`}>
                {persons.map((p, i) => {
                  const sx = (p?.sex || "").toString().toLowerCase();
                  const genderLabel = SEX_LABEL[sx] || "Unspecified";
                  const ageDisplay =
                    typeof p?.age === "number" && !isNaN(p.age)
                      ? `${p.age} years old`
                      : p?.age
                        ? String(p.age)
                        : "Age not provided";
                  return (
                    <li
                      key={`p-${v.id}-${i}`}
                      className="text-sm text-white/90 flex items-start gap-2"
                    >
                      <span className="font-mono text-white/50 mt-0.5 min-w-[36px]">
                        {i === 0 ? "Lead" : `P${i + 1}`}
                      </span>
                      <span>
                        <span
                          className={
                            sx === "female"
                              ? "text-pink-300"
                              : sx === "male"
                                ? "text-sky-300"
                                : "text-white/70"
                          }
                        >
                          {genderLabel}
                        </span>
                        <span className="text-white/50"> · </span>
                        <span className="text-white/95">{ageDisplay}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : legacyBrackets.length > 0 ? (
              <div
                className="text-sm text-white/75 italic"
                data-testid={`visitor-attendees-${v.visitor_ref}`}
              >
                Legacy age brackets:{" "}
                {legacyBrackets
                  .map((b) => AGE_BRACKET_LABEL[b] || b)
                  .join(" · ")}
              </div>
            ) : (
              <div className="text-sm text-white/40">
                No attendee breakdown recorded.
              </div>
            )}
          </div>
        </DetailSection>

        {/* Assistance */}
        <DetailSection icon={UserCheck} title="Special assistance">
          <DetailRow
            label="Requires assistance"
            value={
              assistAns ? (
                <span
                  className={
                    assistAns === "yes"
                      ? "inline-block px-2 py-0.5 bg-amber-500 text-black text-xs font-extrabold uppercase tracking-wider"
                      : "inline-block px-2 py-0.5 bg-white/10 text-white/85 text-xs font-bold uppercase tracking-wider"
                  }
                >
                  {ASSIST_ANSWER[assistAns] || assistAns}
                </span>
              ) : (
                <span className="text-white/40 italic">
                  Question not answered
                </span>
              )
            }
          />
          {assistAns === "yes" && (
            <>
              <DetailRow
                label="Type of assistance"
                value={
                  assistNeeds.length > 0 ? (
                    <ul className="list-disc pl-5 text-white/90">
                      {assistNeeds.map((n) => (
                        <li key={n}>{ASSIST_NEED[n] || n}</li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-white/50 italic">
                      No specific need selected
                    </span>
                  )
                }
              />
              <DetailRow
                label="Additional details"
                value={
                  assistNote ? (
                    <div className="text-white/90 whitespace-pre-wrap break-words">
                      {assistNote}
                    </div>
                  ) : (
                    <span className="text-white/50 italic">
                      No note provided
                    </span>
                  )
                }
              />
            </>
          )}
        </DetailSection>

        {/* Optional interest questions */}
        <DetailSection icon={HelpCircle} title="Optional interest questions">
          <DetailRow
            label="Which parts of the event are you most interested in?"
            value={renderMultiAnswer(exp, IEXP_LABEL)}
          />
          <DetailRow
            label="Which types of vehicles interest you most?"
            value={renderMultiAnswer(veh, IVEH_LABEL)}
          />
          {mul.length > 0 && (
            <DetailRow
              label="Multi-day interests (legacy question)"
              value={renderMultiAnswer(mul, IMUL_LABEL)}
            />
          )}
        </DetailSection>

        {/* Budgets */}
        <DetailSection icon={Wallet} title="Budget questions (single-select)">
          <DetailRow
            label="Vehicle-purchase budget (next 12 months)"
            value={renderSingleAnswer(budP, BUDP_LABEL)}
          />
          <DetailRow
            label="Vehicle-modification budget (next 12 months)"
            value={renderSingleAnswer(budM, BUDM_LABEL)}
          />
        </DetailSection>

        {/* Attendance & check-in */}
        <DetailSection icon={Calendar} title="Attendance and check-in">
          <DetailRow
            label="Planned attendance days"
            value={
              plannedDays.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {plannedDays.map((d) => (
                    <span
                      key={d}
                      className="inline-block px-2.5 py-1 border border-white/20 bg-black/40 text-sm"
                    >
                      {DAY_LABEL_FULL[d] || d}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-white/50 italic">No days selected</span>
              )
            }
          />
          <div className="mt-2">
            <div className="text-xs uppercase tracking-wider text-white/50 mb-2">
              Check-in status by day
            </div>
            <ul className="space-y-1.5">
              {DAYS.map((d) => {
                const at = checked[d];
                return (
                  <li
                    key={d}
                    className="text-sm text-white/90 flex items-start gap-2"
                  >
                    <span className="text-white/60 min-w-[210px]">
                      {DAY_LABEL_FULL[d]}
                    </span>
                    {at ? (
                      <span className="inline-flex items-center gap-1 text-[#22C55E]">
                        <CheckCircle2 className="w-4 h-4" />
                        Checked in · {formatCheckinTimestamp(at)}
                      </span>
                    ) : (
                      <span className="text-white/50">Not checked in yet</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </DetailSection>
      </div>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children }) {
  return (
    <section className="min-w-0">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-[#F5C518]" />}
        <h3 className="text-sm font-bold uppercase tracking-widest text-[#F5C518]">
          {title}
        </h3>
      </div>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="text-sm leading-relaxed">
      <div className="text-xs uppercase tracking-wider text-white/55 mb-0.5">
        {label}
      </div>
      <div className="text-white/95">{value}</div>
    </div>
  );
}

function renderMultiAnswer(keys, labelMap) {
  if (!keys || keys.length === 0) {
    return (
      <span className="text-white/50 italic">Not selected — question skipped</span>
    );
  }
  return (
    <ul className="list-disc pl-5 text-white/90 marker:text-[#F5C518]">
      {keys.map((k) => (
        <li key={k}>{labelMap[k] || k}</li>
      ))}
    </ul>
  );
}

function renderSingleAnswer(key, labelMap) {
  if (!key) {
    return (
      <span className="text-white/50 italic">Not selected — question skipped</span>
    );
  }
  return <span className="text-white/95">{labelMap[key] || key}</span>;
}
